/**
  © BlockyApps. You are permitted to use this content within your store. Redistribution or use in any other application is strictly prohibited.
  Unauthorized copying, distribution, or reproduction in any form will result in legal action.
**/
if (!customElements.get('blocky-video')) {
  customElements.define(
    'blocky-video',
    class BlockyVideo extends HTMLElement {
      constructor() {
        super()

        this.video = null
        this.button = null
        this.spinner = null
        this.isManual = false

        this.visibilityHandler = this.handleVisibilityChange.bind(this)
        this.clickHandler = this.onClick.bind(this)
        this.boundHandleLoaded = this.handleLoaded.bind(this)
        this.boundHandlePlaying = this.handlePlaying.bind(this)
        this.boundHandlePause = this.handlePause.bind(this)
        this.boundHandleEnded = this.handleEnded.bind(this)
        this.boundHandleError = this.handleError.bind(this)

        this.boundProductUiHandler = this.handlePossibleProductUiChange.bind(this)

        this.resumeTimers = []
        this.manualSpinnerTimeout = null
        this.domObserver = null
        this.connected = false
      }

      connectedCallback() {
        if (this.connected) return
        this.connected = true

        this.isManual = this.classList.contains('blocky-video-manual')

        this.captureElements()
        this.bindCurrentVideo()
        this.bindGlobalRecoveryHooks()
        this.observeOwnDom()

        if (!this.isManual) {
          this.scheduleResumePlayback()
        }
      }

      disconnectedCallback() {
        this.connected = false
        this.unbindCurrentVideo()

        if (this.domObserver) {
          this.domObserver.disconnect()
          this.domObserver = null
        }

        document.removeEventListener('visibilitychange', this.visibilityHandler)

        document.removeEventListener('variant:change', this.boundProductUiHandler, true)
        document.removeEventListener('product-info:variant-change', this.boundProductUiHandler, true)
        document.removeEventListener('change', this.boundProductUiHandler, true)
        document.removeEventListener('click', this.boundProductUiHandler, true)

        this.clearTimers()
      }

      captureElements() {
        this.video = this.querySelector('video')
        this.button = this.querySelector('.blocky-video-play-button')
        this.spinner = this.querySelector('.blocky-video-spinner')
      }

      bindCurrentVideo() {
        this.captureElements()
        if (!this.video) return

        this.setupVideo()

        this.video.addEventListener('loadeddata', this.boundHandleLoaded)
        this.video.addEventListener('canplay', this.boundHandleLoaded)
        this.video.addEventListener('playing', this.boundHandlePlaying)
        this.video.addEventListener('pause', this.boundHandlePause)
        this.video.addEventListener('ended', this.boundHandleEnded)
        this.video.addEventListener('error', this.boundHandleError)

        if (this.isManual) {
          this.video.addEventListener('click', this.clickHandler)
          if (this.button) this.button.addEventListener('click', this.clickHandler)
          this.showButton()
          this.hideSpinner()
        } else {
          this.hideButton()
          this.hideSpinner()
        }
      }

      unbindCurrentVideo() {
        if (this.video) {
          this.video.removeEventListener('loadeddata', this.boundHandleLoaded)
          this.video.removeEventListener('canplay', this.boundHandleLoaded)
          this.video.removeEventListener('playing', this.boundHandlePlaying)
          this.video.removeEventListener('pause', this.boundHandlePause)
          this.video.removeEventListener('ended', this.boundHandleEnded)
          this.video.removeEventListener('error', this.boundHandleError)
          this.video.removeEventListener('click', this.clickHandler)
        }

        if (this.button) {
          this.button.removeEventListener('click', this.clickHandler)
        }
      }

      setupVideo() {
        if (!this.video) return

        this.video.muted = true
        this.video.defaultMuted = true
        this.video.playsInline = true
        this.video.loop = true
        this.video.preload = 'metadata'

        this.video.setAttribute('muted', '')
        this.video.setAttribute('playsinline', '')
        this.video.setAttribute('webkit-playsinline', '')
        this.video.setAttribute('loop', '')
        this.video.setAttribute('preload', 'metadata')

        if (this.isManual) {
          this.video.autoplay = false
          this.video.removeAttribute('autoplay')
        } else {
          this.video.autoplay = true
          this.video.setAttribute('autoplay', '')
        }
      }

      hasSource() {
        if (!this.video) return false

        if (this.video.getAttribute('src')) return true

        return Array.from(this.video.querySelectorAll('source')).some((source) => {
          return !!source.getAttribute('src')
        })
      }

      isActuallyVisible() {
        if (!this.isConnected || !this.video) return false

        const style = window.getComputedStyle(this)
        if (style.display === 'none' || style.visibility === 'hidden') return false

        const rect = this.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return false

        return true
      }

      tryRefreshReferences() {
        const currentVideo = this.querySelector('video')
        const currentButton = this.querySelector('.blocky-video-play-button')
        const currentSpinner = this.querySelector('.blocky-video-spinner')

        const videoChanged = currentVideo !== this.video
        const buttonChanged = currentButton !== this.button
        const spinnerChanged = currentSpinner !== this.spinner

        if (!videoChanged && !buttonChanged && !spinnerChanged) return

        this.unbindCurrentVideo()
        this.video = currentVideo
        this.button = currentButton
        this.spinner = currentSpinner
        this.bindCurrentVideo()
      }

      async ensurePlayback() {
        if (this.isManual) return

        this.tryRefreshReferences()

        if (!this.video || !this.hasSource()) return
        if (document.hidden) return
        if (!this.isActuallyVisible()) return

        try {
          if (this.video.readyState === 0) {
            this.video.load()
          }

          const playPromise = this.video.play()
          if (playPromise && typeof playPromise.then === 'function') {
            await playPromise
          }
        } catch (_) {
          // Ignore autoplay rejection and keep retry schedule.
        }
      }

      scheduleResumePlayback() {
        if (this.isManual) return

        this.clearResumeTimers()

        const delays = [0, 80, 200, 400, 800, 1400]

        delays.forEach((delay) => {
          const timer = setTimeout(() => {
            this.ensurePlayback()
          }, delay)

          this.resumeTimers.push(timer)
        })

        requestAnimationFrame(() => {
          this.ensurePlayback()
        })
      }

      bindGlobalRecoveryHooks() {
        document.addEventListener('visibilitychange', this.visibilityHandler)

        // Theme-specific hooks can vary, so we listen to several common paths.
        document.addEventListener('variant:change', this.boundProductUiHandler, true)
        document.addEventListener('product-info:variant-change', this.boundProductUiHandler, true)

        // Generic fallbacks for themes that only update via form/input/media interactions.
        document.addEventListener('change', this.boundProductUiHandler, true)
        document.addEventListener('click', this.boundProductUiHandler, true)
      }

      handlePossibleProductUiChange(event) {
        if (this.isManual) return

        const target = event && event.target ? event.target : null
        if (!target) {
          this.scheduleResumePlayback()
          return
        }

        const relevant =
          target.closest('variant-selects, variant-radios, product-form, .product, .product__media-wrapper, .thumbnail, .slider-button, [name="id"]')

        if (relevant) {
          this.scheduleResumePlayback()
        }
      }

      observeOwnDom() {
        this.domObserver = new MutationObserver((mutations) => {
          let shouldRecover = false

          for (const mutation of mutations) {
            if (mutation.type === 'childList') {
              const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes]
              const hasRelevantNode = changedNodes.some((node) => {
                return (
                  node.nodeType === 1 &&
                  (
                    node.matches?.('video, source, .blocky-video-play-button, .blocky-video-spinner') ||
                    node.querySelector?.('video, source, .blocky-video-play-button, .blocky-video-spinner')
                  )
                )
              })

              if (hasRelevantNode) {
                shouldRecover = true
                break
              }
            }

            if (mutation.type === 'attributes') {
              if (mutation.target?.matches?.('video, source')) {
                shouldRecover = true
                break
              }
            }
          }

          if (shouldRecover) {
            this.tryRefreshReferences()
            this.scheduleResumePlayback()
          }
        })

        this.domObserver.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'poster', 'type']
        })
      }

      handleVisibilityChange() {
        if (!document.hidden) {
          this.scheduleResumePlayback()
        }
      }

      handleLoaded() {
        this.hideSpinner()

        if (this.isManual && this.video?.paused) {
          this.showButton()
        }
      }

      handlePlaying() {
        if (!this.video) return
        this.video.classList.add('blocky-video-playing')
        this.hideSpinner()
        this.hideButton()
      }

      handlePause() {
        if (!this.video) return

        this.video.classList.remove('blocky-video-playing')

        if (this.isManual) {
          this.hideSpinner()
          this.showButton()
        } else {
          // On autoplay videos, if pause happens because of variant/media UI changes,
          // try again after the DOM settles.
          this.scheduleResumePlayback()
        }
      }

      handleEnded() {
        if (!this.video) return

        this.hideSpinner()

        if (this.isManual) {
          this.video.classList.remove('blocky-video-playing')
          this.showButton()

          try {
            this.video.currentTime = 0
          } catch (_) {}
        }
      }

      handleError() {
        if (!this.video) return

        this.classList.add('blocky-video-error')
        this.video.classList.remove('blocky-video-playing')
        this.hideSpinner()
        this.showButtonIfManual()

        if (!this.isManual) {
          this.scheduleResumePlayback()
        }
      }

      onClick(event) {
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }

        if (!this.video) return

        if (this.video.paused) {
          this.hideButton()
          this.showSpinner()

          clearTimeout(this.manualSpinnerTimeout)
          this.manualSpinnerTimeout = setTimeout(() => {
            this.hideSpinner()
          }, 2000)

          const playPromise = this.video.play()

          if (playPromise && typeof playPromise.then === 'function') {
            playPromise
              .then(() => {
                this.video.classList.add('blocky-video-playing')
                this.hideSpinner()
                this.hideButton()
              })
              .catch(() => {
                this.video.classList.remove('blocky-video-playing')
                this.hideSpinner()
                this.showButton()
              })
          } else {
            this.video.classList.add('blocky-video-playing')
            this.hideSpinner()
            this.hideButton()
          }
        } else {
          this.video.pause()
          this.video.classList.remove('blocky-video-playing')
          this.hideSpinner()
          this.showButton()
        }
      }

      showSpinner() {
        if (this.spinner) this.spinner.classList.remove('blocky-hidden')
      }

      hideSpinner() {
        if (this.spinner) this.spinner.classList.add('blocky-hidden')
      }

      showButton() {
        if (this.button) this.button.classList.remove('blocky-hidden')
      }

      hideButton() {
        if (this.button) this.button.classList.add('blocky-hidden')
      }

      showButtonIfManual() {
        if (this.isManual) this.showButton()
      }

      clearResumeTimers() {
        this.resumeTimers.forEach((timer) => clearTimeout(timer))
        this.resumeTimers = []
      }

      clearTimers() {
        this.clearResumeTimers()
        clearTimeout(this.manualSpinnerTimeout)
      }
    }
  )
}